/**
 * @fileoverview Main Kick API client
 * @version 1.0.0
 * @author Kick Flow Team
 */

import type {
	KickFlowConfig,
	OAuthAuthorizationParams,
	OAuthToken,
	OAuthTokenRequest,
} from "./types";
import { CategoriesModule } from './modules/categories';
import { ChannelsModule } from './modules/channels';
import { LivestreamsModule } from './modules/livestreams';
import { ChatModule } from './modules/chat';
import { PKCEUtils } from './common/pkce';
import { OAuthManager } from './common/oauth-manager';
import { HttpClient } from './common/http-client';

/**
 * Main Kick API client
 * 
 * Provides access to all Kick.com API endpoints with automatic OAuth 2.1 management,
 * PKCE support, and comprehensive error handling.
 * 
 * @example Basic usage (server-to-server)
 * ```typescript
 * const client = new KickFlow({
 *   clientId: 'your-client-id',
 *   clientSecret: 'your-client-secret',
 * });
 * 
 * const channel = await client.channels.getChannel('username');
 * ```
 * 
 * @example User authentication
 * ```typescript
 * const client = new KickFlow({
 *   clientId: 'your-client-id',
 *   clientSecret: 'your-client-secret',
 *   redirectUri: 'http://localhost:3000/callback',
 * });
 * 
 * const pkceParams = client.generatePKCEParams();
 * const authUrl = client.getAuthorizationUrl(pkceParams);
 * // Redirect user to authUrl...
 * 
 * const token = await client.exchangeCodeForToken({
 *   code: 'authorization-code-from-callback',
 *   codeVerifier: pkceParams.codeVerifier,
 * });
 * ```
 */
export class KickFlow {
	private readonly config: KickFlowConfig;
	private readonly oauthManager: OAuthManager;
	private readonly httpClient: HttpClient;

	/** Categories API module */
	public readonly categories: CategoriesModule;
	/** Channels API module */
	public readonly channels: ChannelsModule;
	/** Livestreams API module */
	public readonly livestreams: LivestreamsModule;
	/** Chat API module */
	public readonly chat: ChatModule;

	/**
	 * Create a new Kick API client
	 * 
	 * @param config - Client configuration
	 */
	constructor(config: KickFlowConfig) {
		this.config = {
			baseUrl: 'https://api.kick.com',
			oauthUrl: 'https://id.kick.com',
			...config,
		};

		// Initialize core components
		this.oauthManager = new OAuthManager(this.config, this.config.debug);
		this.httpClient = new HttpClient(this.config, this.oauthManager);

		// Initialize API modules
		this.categories = new CategoriesModule(this);
		this.channels = new ChannelsModule(this);
		this.livestreams = new LivestreamsModule(this);
		this.chat = new ChatModule(this);
	}

	/**
	 * Generate PKCE parameters for OAuth authorization
	 * 
	 * @returns Object containing code verifier, challenge, and state
	 * 
	 * @example
	 * ```typescript
	 * const pkceParams = client.generatePKCEParams();
	 * const authUrl = client.getAuthorizationUrl(pkceParams);
	 * ```
	 */
	generatePKCEParams(): OAuthAuthorizationParams {
		return PKCEUtils.generateParams();
	}

	/**
	 * Generate OAuth authorization URL for user authentication
	 * 
	 * @param params - PKCE parameters from generatePKCEParams()
	 * @param scopes - OAuth scopes to request (default: ['public'])
	 * @returns Authorization URL for user to visit
	 * 
	 * @example
	 * ```typescript
	 * const pkceParams = client.generatePKCEParams();
	 * const authUrl = client.getAuthorizationUrl(pkceParams, ['public', 'chat:write']);
	 * console.log('Visit this URL:', authUrl);
	 * ```
	 */
	getAuthorizationUrl(
		params: OAuthAuthorizationParams,
		scopes: string[] = ['public']
	): string {
		return this.oauthManager.getAuthorizationUrl(params, scopes);
	}

	/**
	 * Exchange authorization code for access token
	 * 
	 * @param tokenRequest - Authorization code and PKCE verifier
	 * @returns OAuth token with access/refresh tokens
	 * 
	 * @example
	 * ```typescript
	 * const token = await client.exchangeCodeForToken({
	 *   code: 'authorization-code-from-callback',
	 *   codeVerifier: pkceParams.codeVerifier,
	 * });
	 * ```
	 */
	async exchangeCodeForToken(tokenRequest: OAuthTokenRequest): Promise<OAuthToken> {
		return this.oauthManager.exchangeCodeForToken(tokenRequest);
	}

	/**
	 * Set token manually (useful for stored tokens)
	 * 
	 * @param token - OAuth token to set
	 * 
	 * @example
	 * ```typescript
	 * const storedToken = loadTokenFromStorage();
	 * client.setToken(storedToken);
	 * ```
	 */
	setToken(token: OAuthToken): void {
		this.oauthManager.setToken(token);
	}

	/**
	 * Get current token object
	 * 
	 * @returns Current token or null if not authenticated
	 * 
	 * @example
	 * ```typescript
	 * const token = client.getCurrentToken();
	 * if (token) {
	 *   saveTokenToStorage(token);
	 * }
	 * ```
	 */
	getCurrentToken(): OAuthToken | null {
		return this.oauthManager.getCurrentToken();
	}

	/**
	 * Check if current token is valid
	 * 
	 * @returns True if token exists and is not expired
	 * 
	 * @example
	 * ```typescript
	 * if (!client.isTokenValid()) {
	 *   // Trigger re-authentication
	 *   await reauthenticate();
	 * }
	 * ```
	 */
	isTokenValid(): boolean {
		return this.oauthManager.isTokenValid();
	}

	/**
	 * Clear current token (log out)
	 * 
	 * @example
	 * ```typescript
	 * client.clearToken();
	 * // User is now logged out
	 * ```
	 */
	clearToken(): void {
		this.oauthManager.clearToken();
	}

	/**
	 * Make authenticated HTTP request to the Kick API
	 * 
	 * This method is used internally by the API modules.
	 * For most use cases, use the specific module methods instead.
	 * 
	 * @param endpoint - API endpoint (without base URL)
	 * @param options - Fetch options
	 * @returns Promise resolving to response data
	 * 
	 * @internal
	 */
	async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
		return this.httpClient.request<T>(endpoint, options);
	}
}
